import { useState } from "react";
import { Mail, Phone, User, FileText, MessageSquare, ArrowRight, AlertCircle } from "lucide-react";
import { FadeUp, SectionLabel } from "./Motion";
import { submitCareerApplication } from "../services/careersApi";

export default function CareersForm() {
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    resume: null,
    coverLetter: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^[+]?[0-9\s\-()]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.resume) {
      newErrors.resume = "Resume upload is required";
    } else {
      const validTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
      if (!validTypes.includes(formData.resume.type)) {
        newErrors.resume = "Only PDF and DOC files are accepted";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setFormData((prev) => ({
      ...prev,
      resume: file || null,
    }));
    if (errors.resume) {
      setErrors((prev) => ({
        ...prev,
        resume: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      // Create FormData for multipart/form-data submission (required for file upload)
      const formDataToSend = new FormData();
      formDataToSend.append("fullName", formData.fullName);
      formDataToSend.append("phoneNumber", formData.phoneNumber);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("coverLetter", formData.coverLetter || "");
      formDataToSend.append("resume", formData.resume);

      await submitCareerApplication(formDataToSend);

      setSubmitMessage("Application submitted successfully! We'll be in touch soon.");
      setFormData({
        fullName: "",
        phoneNumber: "",
        email: "",
        resume: null,
        coverLetter: "",
      });
      setErrors({});

      // Clear success message after 5 seconds
      setTimeout(() => setSubmitMessage(""), 5000);
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitMessage(
        error.message || "There was an error submitting your application. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const InputField = ({ label, name, type = "text", icon: Icon, required = false, error }) => (
    <div>
      <label htmlFor={name} className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A] mb-2">
        {Icon && <Icon className="h-4 w-4 text-[#7A2E5D]" />}
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={formData[name]}
        onChange={handleInputChange}
        placeholder={label}
        className={`w-full px-4 py-3 rounded-lg border-2 transition-colors duration-200 focus:outline-none ${
          error
            ? "border-red-500 bg-red-50 focus:border-red-500 focus:bg-white"
            : "border-[#E5E5E5] bg-white focus:border-[#7A2E5D] focus:bg-white focus:ring-1 focus:ring-[#7A2E5D]/20"
        }`}
      />
      {error && (
        <div className="flex items-center gap-1 mt-1.5 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
    </div>
  );

  return (
    <section id="careers" className="py-24 md:py-32 bg-[#FAFAFA]">
      <div className="container mx-auto px-6 md:px-12 max-w-2xl">
        <FadeUp className="text-center mb-16">
          <SectionLabel>Careers</SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl mt-6 tracking-tight text-[#1A1A1A]">
            Join Our <span className="italic brand-gradient-text">Team.</span>
          </h2>
          <p className="mt-6 text-[#555] text-lg max-w-xl mx-auto">
            We're always looking for talented individuals to join our construction team. 
            Submit your application below and let's build something amazing together.
          </p>
        </FadeUp>

        <FadeUp>
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 md:p-12 shadow-sm hover:shadow-lg transition-shadow duration-300">
            {submitMessage && (
              <div className={`mb-6 p-4 rounded-lg border-2 ${
                submitMessage.includes("successfully")
                  ? "bg-green-50 border-green-200 text-green-700"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}>
                {submitMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <InputField
                label="Full Name"
                name="fullName"
                icon={User}
                required
                error={errors.fullName}
              />

              {/* Phone & Email Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Phone Number"
                  name="phoneNumber"
                  type="tel"
                  icon={Phone}
                  required
                  error={errors.phoneNumber}
                />
                <InputField
                  label="Email Address"
                  name="email"
                  type="email"
                  icon={Mail}
                  required
                  error={errors.email}
                />
              </div>

              {/* Resume Upload */}
              <div>
                <label htmlFor="resume" className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A] mb-2">
                  <FileText className="h-4 w-4 text-[#7A2E5D]" />
                  Resume
                  <span className="text-red-500">*</span>
                </label>
                <div
                  className={`relative px-4 py-3 rounded-lg border-2 border-dashed transition-colors duration-200 cursor-pointer ${
                    errors.resume
                      ? "border-red-500 bg-red-50 hover:bg-red-50"
                      : "border-[#7A2E5D]/30 bg-[#7A2E5D]/5 hover:bg-[#7A2E5D]/10"
                  }`}
                >
                  <input
                    id="resume"
                    name="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <div className="text-center py-2">
                    <p className="text-sm font-medium text-[#1A1A1A]">
                      {formData.resume ? formData.resume.name : "Click to upload or drag and drop"}
                    </p>
                    {!formData.resume && (
                      <p className="text-xs text-[#555] mt-1">PDF or DOC files only</p>
                    )}
                  </div>
                </div>
                {errors.resume && (
                  <div className="flex items-center gap-1 mt-1.5 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {errors.resume}
                  </div>
                )}
              </div>

              {/* Cover Letter */}
              <div>
                <label htmlFor="coverLetter" className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A] mb-2">
                  <MessageSquare className="h-4 w-4 text-[#7A2E5D]" />
                  Cover Letter
                  <span className="text-xs text-[#888]">(Optional)</span>
                </label>
                <textarea
                  id="coverLetter"
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleInputChange}
                  placeholder="Tell us about yourself and why you'd like to join our team..."
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border-2 border-[#E5E5E5] bg-white focus:outline-none focus:border-[#7A2E5D] focus:ring-1 focus:ring-[#7A2E5D]/20 transition-colors duration-200 resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 brand-gradient text-white font-medium py-4 rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:translate-y-0"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="text-center text-xs text-[#888] mt-4">
                By submitting, you agree that we may process your data to review your application.
              </p>
            </form>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
